import { useEffect, useMemo, useState } from 'react';
import { ChevronRight, ChevronDown, Play, Square, Plus } from 'lucide-react';
import { useTaskTimeEntryStore } from '../../lib/stores/taskTimeEntryStore';
import { useProjectStore } from '../../lib/stores/projectStore';
import { useAuth } from '../../lib/auth';
import { formatMinutes, parseDuration } from '../../lib/utils/timeFormat';
import { TaskTimeEntryRow } from './TaskTimeEntryRow';

interface Props {
  taskId: string;
  projectId: string;
}

/** Oltre questa soglia un timer acceso è quasi sicuramente stato dimenticato. */
const FORGOTTEN_TIMER_MINUTES = 8 * 60;

/** Blocco "Tempo" dentro il dettaglio della task: timer, voci registrate, aggiunta manuale. */
export function TaskTimeTracker({ taskId, projectId }: Props) {
  const { user } = useAuth();
  const {
    entriesByTask,
    activeTimer,
    fetchEntries,
    fetchActiveTimer,
    startTimer,
    stopTimer,
    createEntry,
    updateEntry,
    deleteEntry,
    totalMinutes
  } = useTaskTimeEntryStore();

  const [expanded, setExpanded] = useState(false);
  const [tick, setTick] = useState(0);
  const [adding, setAdding] = useState(false);
  const [manualDuration, setManualDuration] = useState('');
  const [manualNote, setManualNote] = useState('');
  const [invalidDuration, setInvalidDuration] = useState(false);

  useEffect(() => {
    fetchEntries(taskId);
    fetchActiveTimer();
  }, [taskId, fetchEntries, fetchActiveTimer]);

  // Il contatore si aggiorna ogni trenta secondi: mostrando i minuti, più spesso non serve.
  useEffect(() => {
    if (!activeTimer) return;
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, [activeTimer]);

  const entries = entriesByTask[taskId] ?? [];
  const closedEntries = entries.filter((e) => e.ended_at !== null);
  const total = totalMinutes(taskId);
  const runningHere = activeTimer?.taskId === taskId;

  // Il responsabile del progetto risponde del monte ore verso il cliente, quindi può correggere
  // anche le voci altrui: è la stessa deroga che hanno le policy di update e delete sulla tabella.
  const projects = useProjectStore((s) => s.projects);
  const isProjectManager = projects.some(
    (p) => p.id === projectId && p.project_manager_id === user?.id
  );

  const runningMinutes = useMemo(() => {
    if (!runningHere || !activeTimer) return 0;
    void tick; // il tick serve solo a rivalutare il conteggio
    return Math.round((Date.now() - new Date(activeTimer.startedAt).getTime()) / 60000);
  }, [runningHere, activeTimer, tick]);

  const forgotten = runningMinutes >= FORGOTTEN_TIMER_MINUTES;

  const addManual = async () => {
    const minutes = parseDuration(manualDuration);
    if (!minutes) {
      // senza un segnale il clic su Salva non fa nulla e sembra un guasto
      setInvalidDuration(true);
      return;
    }
    setInvalidDuration(false);

    const now = new Date();
    await createEntry({
      task_id: taskId,
      project_id: projectId,
      started_at: new Date(now.getTime() - minutes * 60000).toISOString(),
      ended_at: now.toISOString(),
      duration_minutes: minutes,
      description: manualNote || null
    });

    setManualDuration('');
    setManualNote('');
    setAdding(false);
  };

  return (
    <div className="mt-2">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.04] transition-colors"
      >
        {expanded ? (
          <ChevronDown className="w-3.5 h-3.5 text-[#787774]" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 text-[#787774]" />
        )}
        <span className="text-sm font-medium text-[#37352F] dark:text-gray-200">Tempo</span>
        <span className="ml-auto text-sm text-[#787774] dark:text-gray-400">
          {formatMinutes(total)}
        </span>
      </button>

      {expanded && (
        <div className="pl-6 pr-2 pb-2">
          {runningHere ? (
            <div className="flex items-center gap-2 py-1.5">
              <button
                onClick={() => stopTimer()}
                className="flex items-center gap-1.5 text-sm text-[#37352F] dark:text-gray-200 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-700 rounded px-1.5 py-1"
              >
                <Square className="w-3.5 h-3.5" /> Ferma
              </button>
              <span
                className={`text-sm ${
                  forgotten
                    ? 'text-amber-600 dark:text-amber-500'
                    : 'text-[#787774] dark:text-gray-400'
                }`}
              >
                {/* Un timer appena avviato vale zero minuti, ma "—" farebbe pensare che non
                    stia contando: qui lo zero si scrive. */}
                {runningMinutes === 0 ? '0m' : formatMinutes(runningMinutes)}
                {forgotten && <span className="ml-2 text-xs">Sembra dimenticato</span>}
              </span>
            </div>
          ) : (
            <button
              onClick={() => startTimer(taskId, projectId)}
              className="flex items-center gap-1.5 py-1.5 text-sm text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-gray-200 transition-colors"
            >
              <Play className="w-3.5 h-3.5" />
              {activeTimer ? 'Avvia (ferma quello in corso)' : 'Avvia'}
            </button>
          )}

          <div className="mt-1">
            {closedEntries.map((entry) => (
              <TaskTimeEntryRow
                key={entry.id}
                entry={entry}
                canEdit={entry.user_id === user?.id || isProjectManager}
                onUpdate={updateEntry}
                onDelete={deleteEntry}
              />
            ))}
          </div>

          {adding ? (
            /* Il motivo del rifiuto sta SOTTO il campo, non nel tooltip: chi scriveva un
               formato non capito vedeva solo il testo ingiallire e credeva a un guasto
               (segnalazione Barf Italia, 15/09/2026). */
            <div className="py-1.5 px-2">
              <div className="flex items-center gap-2">
                <input
                  autoFocus
                  value={manualDuration}
                  onChange={(e) => {
                    setManualDuration(e.target.value);
                    if (invalidDuration) setInvalidDuration(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') addManual();
                    if (e.key === 'Escape') setAdding(false);
                  }}
                  placeholder="1h 30m"
                  aria-invalid={invalidDuration || undefined}
                  aria-describedby={invalidDuration ? 'durata-non-capita' : undefined}
                  className={`w-20 text-sm bg-transparent border-none outline-none focus:ring-0 p-0 ${
                    invalidDuration
                      ? 'text-amber-600 dark:text-amber-500'
                      : 'text-[#37352F] dark:text-gray-200'
                  }`}
                />
                <input
                  value={manualNote}
                  onChange={(e) => setManualNote(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') addManual();
                    if (e.key === 'Escape') setAdding(false);
                  }}
                  placeholder="Che cosa hai fatto"
                  className="flex-1 min-w-0 text-sm bg-transparent border-none outline-none focus:ring-0 p-0 text-[#37352F] dark:text-gray-200"
                />
                <button
                  onClick={addManual}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Salva
                </button>
                <button
                  onClick={() => setAdding(false)}
                  className="text-xs text-[#787774] hover:underline"
                >
                  Annulla
                </button>
              </div>
              {invalidDuration && (
                <p
                  id="durata-non-capita"
                  role="alert"
                  className="mt-1 text-[11.5px] leading-snug text-amber-600 dark:text-amber-500"
                >
                  Non ho capito la durata. Scrivila come 1h 30m, 1:30, 2 ore, 30 min oppure 90.
                </p>
              )}
            </div>
          ) : (
            <button
              onClick={() => setAdding(true)}
              className="flex items-center gap-1.5 py-1.5 px-2 text-sm text-[#787774] dark:text-gray-500 hover:text-[#37352F] dark:hover:text-gray-300 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Registra tempo
            </button>
          )}
        </div>
      )}
    </div>
  );
}
